/**
 * useCoalescedPost — send a whole-session POST (lifecycle, video, mediation, bid
 * health) at most once at a time and at most every `minIntervalMs`, always with
 * the latest rows.
 *
 * Each of those views re-posts every row whenever `data` changes. Under live
 * traffic (~1 request/s) a 400ms debounce meant a new ~10 MB POST almost every
 * second while the last was still in flight (2.5s round trip on 2,000 rows), and
 * the serialise/parse kept the main thread ~50% busy — the dashboard "kept
 * loading" (2026-10-19). Coalescing changes that arrive mid-flight into one
 * follow-up send keeps the answer fresh without stacking requests.
 *
 * `send` returns a promise (or nothing). It is read through a ref, so it always
 * sees the current render's rows. `deps` mark the input changed; `enabled:false`
 * drops anything pending (the caller handles its own empty state).
 * `urgentDeps` are user choices (a scope switch): a change there skips the
 * interval — it still waits for an in-flight POST, never stacks one.
 */
import { useEffect, useRef } from 'react';

export const COALESCED_POST_DEBOUNCE_MS = 400;
export const COALESCED_POST_MIN_INTERVAL_MS = 3000;

export function createCoalescedPoster({ debounceMs = COALESCED_POST_DEBOUNCE_MS, minIntervalMs = COALESCED_POST_MIN_INTERVAL_MS, now = () => Date.now(), setTimer = setTimeout, clearTimer = clearTimeout } = {}) {
  let timer = null;
  let inFlight = false;
  let dirty = false;
  let lastStart = -Infinity;
  let stopped = false;
  let send = () => undefined;

  const kick = () => {
    if (stopped || !dirty || inFlight || timer) return;
    const wait = Math.max(debounceMs, lastStart + minIntervalMs - now());
    timer = setTimer(fire, wait);
  };
  function fire() {
    timer = null;
    if (stopped || !dirty) return;
    dirty = false;
    inFlight = true;
    lastStart = now();
    let p;
    try { p = Promise.resolve(send()); } catch { p = Promise.resolve(); }
    p.catch(() => {}).then(() => { inFlight = false; kick(); });
  }

  return {
    setSend(fn) { send = fn; },
    changed({ urgent = false } = {}) {
      dirty = true;
      if (urgent) {
        lastStart = -Infinity;
        if (timer) { clearTimer(timer); timer = null; }
      }
      kick();
    },
    cancel() { dirty = false; if (timer) { clearTimer(timer); timer = null; } },
    start() { stopped = false; },
    stop() { stopped = true; dirty = false; if (timer) { clearTimer(timer); timer = null; } },
    get inFlight() { return inFlight; },
  };
}

export default function useCoalescedPost(send, deps, { enabled = true, debounceMs, minIntervalMs, urgentDeps = [] } = {}) {
  const ref = useRef(null);
  if (!ref.current) ref.current = createCoalescedPoster({ debounceMs, minIntervalMs });
  ref.current.setSend(send);
  // start() too: StrictMode mounts, unmounts and remounts in development.
  useEffect(() => { ref.current.start(); return () => ref.current.stop(); }, []);
  const urgentKey = useRef(urgentDeps);
  useEffect(() => {
    const urgent = urgentDeps.some((d, i) => !Object.is(d, urgentKey.current[i]));
    urgentKey.current = urgentDeps;
    if (enabled) ref.current.changed({ urgent });
    else ref.current.cancel();
  }, [enabled, ...deps, ...urgentDeps]); // eslint-disable-line react-hooks/exhaustive-deps
}
